import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, BadgeCheck, Building2, FileText, Landmark, Search, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import MainLayout from "@/components/MainLayout";
import RecordTag from "@/components/RecordTag";
import RexpertLogo from "@/components/RexpertLogo";
import StatusBadge from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { publicApi } from "@/lib/api";

const heroImage = "https://hvkurz.de/images/oeffentlich-hero.png";
const trustImage = "https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const expertImage = "https://images.unsplash.com/photo-1694702702714-a48c5fabdaf3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nfGVufDB8fHx8MTc3Njk0NjAxM3ww&ixlib=rb-4.1.0&q=85";

const specialties = ["Alle", "Verkehrswertgutachten", "Bauschäden", "Schiedsgutachten", "Marktanalysen"];

const trustPoints = [
  "Aufnahme nur nach geordnetem Prüfungsverfahren",
  "Sichtbare REEXPERTS-Kennung im Verbandsauftritt",
  "Rechts- und Verfahrensgrundlagen offen einsehbar",
];

const conversionCards = [
  {
    title: "Geprüfte Aufnahme",
    copy: "Drei Referenzgutachten und klare fachliche Prüfung vor Freigabe.",
    icon: ShieldCheck,
  },
  {
    title: "Sichtbare Kennung",
    copy: "Mitglieder werden mit REEXPERTS-Kennung im öffentlichen Auftritt geführt.",
    icon: BadgeCheck,
  },
  {
    title: "Verbindliche Ordnung",
    copy: "Satzung, Ordnungen und Beschwerdewege bleiben nachvollziehbar geregelt.",
    icon: FileText,
  },
];

const legalTeasers = [
  {
    title: "Satzung der Vereinigung",
    text: "Grundordnung zu Mitgliedschaft, Rechten, Pflichten und Zweck.",
    path: "/rechtliches#satzung",
    cta: "Zur Satzung",
  },
  {
    title: "Verfahrensordnung",
    text: "Regelt Aufnahme, Beschwerden, Austritt und Entzug.",
    path: "/verfahrensordnung",
    cta: "Zur Verfahrensordnung",
  },
  {
    title: "Beitragsordnung",
    text: "Ordnet Fälligkeit, Freigabe, Mahnung und Beitragsführung.",
    path: "/beitragsordnung",
    cta: "Zur Beitragsordnung",
  },
  {
    title: "Beschwerdestelle",
    text: "Sachliche Eingabe- und Bearbeitungsstrecke für Mitglieder und Mandanten.",
    path: "/beschwerde",
    cta: "Zur Beschwerdestelle",
  },
];

export default function HomePage({ session, onLogout }) {
  const [content, setContent] = useState(null);
  const [experts, setExperts] = useState([]);
  const [search, setSearch] = useState("");
  const [specialty, setSpecialty] = useState("Alle");
  const [sort, setSort] = useState("name");

  useEffect(() => {
    const load = async () => {
      const data = await publicApi.getContent();
      setContent(data);
      setExperts(data.experts || []);
    };
    load();
  }, []);

  const handleSearch = async () => {
    const response = await publicApi.searchExperts({ q: search, specialty, sort });
    setExperts(response.items || []);
  };

  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="Vereinigung freier Immobiliensachverständiger"
      pageDescription="REEXPERTS – Vereinigung freier Immobiliensachverständiger mit Mitgliedsantrag, Experten-Suche, Ordnungen und Verwaltungsstruktur."
    >
      <div className="bg-[#F8F9FA] text-slate-900">
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="border-b border-slate-200 bg-white"
          data-testid="home-hero-section"
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 py-16 lg:px-8 lg:py-20">
            <div className="space-y-7">
              <div className="relative min-h-[520px] overflow-hidden rounded-[1.9rem] border border-slate-200 bg-white shadow-sm sm:min-h-[560px]" data-testid="home-hero-image-card">
                <img src={heroImage} alt="Öffentliche Verwaltung und moderne Liegenschaft" className="absolute inset-0 h-full w-full object-cover" data-testid="home-hero-image" />
                <div className="absolute inset-0 bg-white/80 sm:hidden" />
                <div className="absolute inset-0 hidden bg-[linear-gradient(90deg,rgba(248,249,250,0.96)_0%,rgba(248,249,250,0.92)_42%,rgba(248,249,250,0.52)_70%,rgba(248,249,250,0.14)_100%)] sm:block" />
                <div className="relative z-10 flex h-full flex-col justify-center gap-5 p-6 sm:p-8 lg:p-12">
                  <Badge className="w-fit max-w-[260px] border-slate-200 bg-white text-slate-700 shadow-sm sm:max-w-none" data-testid="home-hero-badge">
                    <span className="mr-3 inline-flex items-center gap-1">
                      <span className="h-2 w-4 rounded-full bg-[#2f2f2f]" />
                      <span className="h-2 w-4 rounded-full bg-[#a8171b]" />
                      <span className="h-2 w-4 rounded-full bg-[#d4af37]" />
                    </span>
                    <span className="sm:hidden">Vertrauen & Mitgliedschaft</span>
                    <span className="hidden sm:inline">Vertrauen und Mitgliedschaft im D-A-CH Raum</span>
                  </Badge>

                  <div className="space-y-4 max-w-3xl">
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#8a6f3b] sm:text-xs sm:tracking-[0.3em]" data-testid="home-hero-kicker">
                      <span className="sm:hidden">Prüfung · Sichtbarkeit</span>
                      <span className="hidden sm:inline">Vereinigung · Prüfung · Sichtbarkeit</span>
                    </p>
                    <h1 className="max-w-4xl text-[1.92rem] font-extrabold leading-[0.98] text-slate-900 sm:text-5xl xl:text-6xl" data-testid="home-hero-heading">
                      Die Vereinigung für qualifizierte Immobilien-Expertise.
                    </h1>
                    <p className="max-w-2xl text-base leading-relaxed text-slate-700 md:text-lg" data-testid="home-hero-description">
                      REALESTATEEXPERTS vereint freie Immobiliensachverständige, Bauschaden-Gutachter, Verkehrswert-Experten und qualifizierte Makler mit sachverständigenbezogener Weiterbildung in einer klar geführten Verbandsstruktur.
                    </p>
                    <p className="max-w-2xl text-[11px] uppercase tracking-[0.16em] text-[#8a6f3b] sm:text-sm sm:tracking-[0.2em]" data-testid="home-hero-name-explanation">
                      REEXPERTS ist die Kurzform von REAL ESTATE EXPERTS.
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
                    <Link to="/apply" data-testid="home-hero-apply-link">
                      <Button className="w-full rounded-full bg-slate-900 px-7 text-white hover:bg-slate-800 sm:w-auto" data-testid="hero-cta-member">
                        Mitglied werden <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <button
                      onClick={() => document.querySelector('[data-testid="home-expert-directory-section"]')?.scrollIntoView({ behavior: 'smooth' })}
                      className="inline-flex w-full items-center justify-center rounded-full border border-slate-300 bg-white px-7 py-2 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:bg-slate-50 sm:w-auto"
                      data-testid="hero-cta-expert-search"
                    >
                      Experten finden
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid max-w-3xl gap-3" data-testid="home-trust-points-grid">
                {trustPoints.map((point, index) => (
                  <div key={point} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`home-trust-point-${index + 1}`}>
                    <p className="text-sm leading-relaxed text-slate-700">{point}</p>
                  </div>
                ))}
              </div>

            </div>

            <div className="space-y-5">
              <div className="grid gap-4">
                <Card className="border-slate-200 bg-slate-900 text-white shadow-sm" data-testid="home-hero-seal-card">
                  <CardContent className="space-y-5 p-6">
                    <RexpertLogo dark testId="home-hero-logo" />
                    <div>
                      <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#C5A059]">Vereinssiegel</p>
                      <p className="mt-3 text-base leading-relaxed text-slate-200">Das Verbandssiegel wird als verbindliche Kennzeichnung im öffentlichen Auftritt der Vereinigung geführt.</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 bg-white shadow-sm" data-testid="home-hero-id-card">
                  <CardContent className="space-y-4 p-6">
                    <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#8a6f3b]">Mitgliedskennung</p>
                    <RecordTag value="REEXPERTS-2026-001" testId="home-hero-id-tag" />
                    <p className="text-sm leading-relaxed text-slate-600">Direkt sichtbar in Profilen, Suchergebnissen und internen Vorgängen.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </motion.section>

        <section className="border-b border-slate-200 bg-slate-900 py-4 text-white" data-testid="home-news-marquee-section">
          <Marquee pauseOnHover gradient={false} speed={22}>
            {(content?.news || []).map((item) => (
              <div key={item.id} className="mx-6 flex items-center gap-3" data-testid={`home-marquee-item-${item.id}`}>
                <Sparkles className="h-4 w-4 text-[#C5A059]" />
                <span className="text-sm font-medium">{item.headline}</span>
                <span className="text-xs uppercase tracking-[0.24em] text-slate-400">{item.tag}</span>
              </div>
            ))}
          </Marquee>
        </section>

        <div className="mx-auto flex max-w-7xl flex-col gap-16 px-6 py-16 lg:px-8 lg:py-20">
          <section className="grid gap-6" data-testid="home-trust-membership-section">
            <Card className="border-slate-200 bg-white shadow-sm" data-testid="home-manifesto-main-card">
              <CardContent className="grid gap-5 p-7 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#8a6f3b]" data-testid="home-manifesto-kicker">Mitgliedschaft und Vertrauen</p>
                  <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-900" data-testid="home-manifesto-heading">
                    Mitgliedschaft mit klaren Voraussetzungen und sichtbarem Mehrwert.
                  </h2>
                  <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-700" data-testid="home-manifesto-description">
                    Mitgliedschaft bei REEXPERTS bedeutet nachvollziehbare Aufnahme, sichtbare Kennung, geordnete Weiterbildung und eine qualitätsbezogene Einordnung im öffentlichen Verbandsauftritt.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/apply" data-testid="home-membership-cta-link">
                    <Button className="rounded-full bg-slate-900 text-white hover:bg-slate-800">Mitgliedschaft beantragen</Button>
                  </Link>
                  <Link to="/rechtliches#mitgliedschaft" data-testid="home-membership-cta-secondary-link">
                    <Button variant="outline" className="rounded-full">Mitgliedschaft im Detail</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-6 xl:grid-cols-[0.88fr_1.12fr]">
              <Card className="overflow-hidden border-slate-200 bg-white shadow-sm" data-testid="home-trust-image-panel">
                <CardContent className="space-y-5 p-0">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={trustImage} alt="Vertrauensaufbau" className="h-full w-full object-cover" data-testid="home-trust-image" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/35 to-transparent" />
                  </div>
                  <div className="p-6 pt-0">
                    <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#8a6f3b]" data-testid="home-trust-image-kicker">Vertrauen und Aufnahme</p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-700" data-testid="home-trust-image-copy">
                      Geordnete Prüfung, klare Kennung und nachvollziehbare Verbandszuordnung bilden die Grundlage einer belastbaren Mitgliedschaft.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <div className="grid gap-4 self-start">
                {conversionCards.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Card key={item.title} className="border-slate-200 bg-white shadow-sm" data-testid={`home-conversion-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <CardContent className="flex items-start gap-4 p-5">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-900">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-[1.15rem] font-bold leading-tight text-slate-900">{item.title}</p>
                          <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.copy}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="space-y-8" data-testid="home-expert-directory-section">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#8a6f3b]" data-testid="home-expert-kicker">Sachverständigen-Abfrage</p>
                <h2 className="text-3xl font-bold text-slate-900" data-testid="home-expert-heading">Experten schnell finden und fachlich einordnen.</h2>
              </div>
              <Badge className="border-slate-200 bg-white text-slate-700 shadow-sm" data-testid="home-expert-badge">
                Geprüfte Profile mit Kennung und Fachbezug
              </Badge>
            </div>

            <Card className="border-slate-200 bg-white shadow-sm" data-testid="home-expert-search-card">
              <CardContent className="grid gap-3 p-5 lg:grid-cols-[1.4fr_0.9fr_0.9fr_auto]">
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-slate-500">Suche</label>
                  <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, Ort oder Fachgebiet" data-testid="expert-search-input" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-slate-500">Fachgebiet</label>
                  <select value={specialty} onChange={(event) => setSpecialty(event.target.value)} className="h-10 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900" data-testid="expert-specialty-select">
                    {specialties.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-slate-500">Sortierung</label>
                  <select value={sort} onChange={(event) => setSort(event.target.value)} className="h-10 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900" data-testid="expert-sort-select">
                    <option value="name">Name</option>
                    <option value="city">Ort</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button className="w-full rounded-full bg-slate-900 text-white hover:bg-slate-800" onClick={handleSearch} data-testid="expert-search-button">
                    <Search className="mr-2 h-4 w-4" /> Suchen
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-5 xl:grid-cols-4 lg:grid-cols-2">
              {experts.map((expert, index) => (
                <Card key={expert.id} className="overflow-hidden border-slate-200 bg-white shadow-sm" data-testid={`expert-card-${expert.id}`}>
                  <div className="relative h-64 overflow-hidden">
                    <img src={index % 2 === 0 ? expertImage : trustImage} alt={expert.full_name} className="h-full w-full object-cover" data-testid={`expert-image-${expert.id}`} />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/85 to-transparent p-4">
                      <RecordTag value={expert.rexpert_id} testId={`expert-rexpert-id-${expert.id}`} className="border-[#d4af37]/30 bg-white/90 text-slate-900" />
                    </div>
                  </div>
                  <CardContent className="space-y-4 p-6">
                    <div>
                      <p className="text-[1.7rem] font-bold leading-tight text-slate-900" data-testid={`expert-name-${expert.id}`}>{expert.full_name}</p>
                      <p className="mt-1 text-sm text-slate-600">{expert.title}</p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`expert-city-${expert.id}`}>
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Standort</p>
                        <p className="mt-2 text-sm font-medium text-slate-900">{expert.city}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`expert-status-${expert.id}`}>
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Status</p>
                        <div className="mt-2">
                          <StatusBadge status={expert.badge_status || expert.member_type} testId={`expert-status-badge-${expert.id}`} />
                        </div>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`expert-specialties-${expert.id}`}>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Fachgebiete</p>
                      <p className="mt-2 text-sm leading-relaxed text-slate-700">{expert.specialties?.join(", ")}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="grid gap-8" data-testid="home-legal-section">
            <Card className="border-slate-900 bg-slate-900 text-white shadow-sm" data-testid="home-legal-intro-card">
              <CardContent className="space-y-5 p-8">
                <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#C5A059]">Recht und Vertrauen</p>
                <h2 className="text-3xl font-bold text-white" data-testid="home-legal-heading">Rechtliche Grundlagen, Ordnungen und Beschwerdewege klar strukturiert.</h2>
                <p className="text-base leading-relaxed text-slate-200" data-testid="home-legal-description">
                  Satzung, Verfahrensordnung, Beitragsordnung und Beschwerdestelle werden bewusst nur knapp eingeführt und auf die jeweiligen Detailseiten geführt.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/rechtliches#satzung" data-testid="home-legal-page-link">
                    <Button className="rounded-full bg-[#C5A059] text-slate-900 hover:bg-[#b28f4d]">Zur Satzung</Button>
                  </Link>
                  <Link to="/rechtliches" data-testid="home-legal-overview-link">
                    <Button variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10">Alle Grundlagen</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4">
              {legalTeasers.map((item) => (
                <Card key={item.title} className="border-slate-200 bg-white shadow-sm" data-testid={`legal-preview-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <CardContent className="space-y-4 p-6">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-[#8a6f3b]">Teaser</p>
                      <h3 className="mt-2 text-[1.5rem] font-bold leading-tight text-slate-900">{item.title}</h3>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600">{item.text}</p>
                    <Link to={item.path} data-testid={`legal-preview-link-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <Button variant="outline" className="rounded-full">{item.cta}</Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </div>
      </div>
    </MainLayout>
  );
}